import {
  getAisWebsocketToken,
  getAisWebsocketUrl,
} from '../api/internal-ais-api/internal-ais-api';
import {
  intialiseAisApiWebsocket,
  locationMessagesToHistoricVesselData,
} from '../api/internal-ais-api/internal-ais-api-helpers';
import { HistoryFormValues } from '../maritime-menu-options/history-panel/history-form/history-form-validators';
import {
  setError,
  setVesselHistoryData,
} from '../maritime-menu-options/history-panel/history-panel.slice';
import { LocationMessage } from '../models/vessels/internal-ais-api.model';
import store from '../store';
import { UserPreferences } from '../user-settings/user-preferences/user-preferences.slice';
import {
  buildIdentifiers,
  getRawVesselPoints,
  renderVesselHistory,
} from './vessels.utils';

/**
 * Setup/profile restore entry point for AIS History.
 * Kept in a dedicated file to isolate setup-restore websocket orchestration from
 * shared vessel helpers and the legacy DaaS path, which makes migration cutover
 * changes safer and easier to review.
 */
const CALLER_FEATURE = 'ais-api-history-setup-restore';

interface AisApiWebsocketMessage {
  data?: LocationMessage[];
  metadata?: { endOfData: boolean };
}

const getAisSetupWebsocketUrl = (baseUrl: string, token: string) => {
  const delimiter = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${delimiter}token=${token}`;
};

const getVesselAisApiHistory = async (
  formValues: HistoryFormValues,
  userPreferences: UserPreferences
): Promise<void> => {
  try {
    const formattedIdentifiers = buildIdentifiers(
      formValues.identifiers.split(/\s|\r|\n/g).filter((s) => s !== '')
    );

    const { token } = await getAisWebsocketToken({
      callerFeature: CALLER_FEATURE,
    });
    const { ws_url: websocketBaseUrl } = await getAisWebsocketUrl();

    const { dispatchQuery } = await intialiseAisApiWebsocket(
      null,
      token,
      CALLER_FEATURE,
      undefined,
      new Date(`${formValues.fromDate}T00:00:00Z`),
      new Date(`${formValues.toDate}T23:59:59Z`),
      formattedIdentifiers.mmsis.map(Number),
      formattedIdentifiers.imos.map(Number),
      formValues.sampleRate
    );

    const allMessages: LocationMessage[] = [];

    await new Promise<void>((resolve, reject) => {
      let completed = false;
      const websocket = new WebSocket(
        getAisSetupWebsocketUrl(websocketBaseUrl, token)
      );

      websocket.onopen = () => {
        dispatchQuery()
          .then((ok) => {
            if (!ok) {
              websocket.close();
              reject(
                new Error('Failed to initialise AIS API setup restore request.')
              );
            }
          })
          .catch((error) => {
            websocket.close();
            reject(error);
          });
      };

      websocket.onmessage = (event) => {
        try {
          const response = JSON.parse(
            event.data as string
          ) as AisApiWebsocketMessage;

          if (response.data?.length) {
            allMessages.push(...response.data);
          }

          if (response.metadata?.endOfData || !response.data) {
            completed = true;
            websocket.close();
            resolve();
          }
        } catch (error) {
          websocket.close();
          reject(error);
        }
      };

      websocket.onerror = () => {
        websocket.close();
        reject(new Error('AIS API setup restore websocket error.'));
      };

      websocket.onclose = () => {
        if (!completed) {
          reject(
            new Error('AIS API setup restore websocket closed unexpectedly.')
          );
        }
      };
    });

    const data = locationMessagesToHistoricVesselData(allMessages);
    store.dispatch(
      setVesselHistoryData({
        data,
        formValues,
      })
    );

    const rawVesselPoints = getRawVesselPoints(data);
    renderVesselHistory(rawVesselPoints, userPreferences, {
      prepend: false,
      shouldDisplayOtherVessels: false,
    });
    store.dispatch(setError(false));
  } catch {
    store.dispatch(setError(true));
  }
};

export default getVesselAisApiHistory;
